import { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    MenuItem,
    Stack,
    Chip,
    Grid,
    Alert,
    Snackbar,
    CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { TOOLS } from "../util/config";
import { colors } from "../theme/theme";
import { commentsApi } from "../api/client";

const statusColors = {
    NEW: { bg: "#2563EB15", fg: "#2563EB", label: "New" },
    ACKNOWLEDGED: { bg: "#F59E0B15", fg: "#F59E0B", label: "Acknowledged" },
    IN_PROGRESS: { bg: "#7C3AED15", fg: "#7C3AED", label: "In progress" },
    RESOLVED: { bg: "#0F9D5815", fg: "#0F9D58", label: "Resolved" },
};

export const FeedbackPage = () => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ authorName: "", toolId: "general", body: "" });
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const load = () => {
        setLoading(true);
        commentsApi
            .list()
            .then(setComments)
            .catch(() => setComments([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const submit = async () => {
        if (!form.authorName.trim() || !form.body.trim()) {
            return setSnackbar({ open: true, message: "Name and message are required", severity: "error" });
        }
        setSubmitting(true);
        try {
            await commentsApi.create(form.authorName.trim(), form.toolId, form.body.trim());
            setForm({ authorName: form.authorName, toolId: "general", body: "" });
            setSnackbar({ open: true, message: "Thanks for your feedback!", severity: "success" });
            load();
        } catch (e) {
            setSnackbar({ open: true, message: "Failed to send feedback", severity: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box>
            <Box mb={4}>
                <Typography variant="overline" sx={{ color: colors.textMuted }}>
                    Community
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
                    Feedback wall.
                </Typography>
                <Typography sx={{ color: colors.textMuted, maxWidth: 620 }}>
                    Anyone can leave feedback, requests, or bug reports. All submissions are public.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <Card sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Leave feedback
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    label="Your name"
                                    fullWidth
                                    value={form.authorName}
                                    onChange={(e) => setForm({ ...form, authorName: e.target.value })}
                                />
                                <TextField
                                    label="About which tool?"
                                    select
                                    fullWidth
                                    value={form.toolId}
                                    onChange={(e) => setForm({ ...form, toolId: e.target.value })}
                                >
                                    <MenuItem value="general">General</MenuItem>
                                    {TOOLS.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Your message"
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    value={form.body}
                                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                                />
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={submit}
                                    disabled={submitting}
                                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                                >
                                    {submitting ? "Sending..." : "Send feedback"}
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={7}>
                    {loading ? (
                        <Box textAlign="center" py={6}><CircularProgress /></Box>
                    ) : comments.length === 0 ? (
                        <Card>
                            <CardContent sx={{ p: 4, textAlign: "center" }}>
                                <Typography sx={{ color: colors.textMuted }}>
                                    No feedback yet. Be the first.
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        <Stack spacing={2}>
                            {comments.map((c) => {
                                const status = statusColors[c.status] || statusColors.NEW;
                                return (
                                    <Card key={c.id}>
                                        <CardContent>
                                            <Stack direction="row" alignItems="center" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
                                                <Box
                                                    sx={{
                                                        width: 32, height: 32, borderRadius: "50%",
                                                        bgcolor: colors.surfaceAlt, color: colors.text,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        fontWeight: 700, fontSize: 13,
                                                    }}
                                                >
                                                    {c.authorName?.[0]?.toUpperCase() || "?"}
                                                </Box>
                                                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{c.authorName}</Typography>
                                                <Chip
                                                    label={c.toolId || "general"}
                                                    size="small"
                                                    sx={{
                                                        fontFamily: '"JetBrains Mono"',
                                                        fontSize: 10,
                                                        height: 20,
                                                        bgcolor: colors.surfaceAlt,
                                                    }}
                                                />
                                                <Chip
                                                    label={status.label}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: status.bg,
                                                        color: status.fg,
                                                        fontWeight: 600,
                                                        fontSize: 10,
                                                        height: 20,
                                                    }}
                                                />
                                                <Box flex={1} />
                                                <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: '"JetBrains Mono"' }}>
                                                    {new Date(c.createdAt).toLocaleDateString()}
                                                </Typography>
                                            </Stack>
                                            <Typography sx={{ fontSize: 14.5, lineHeight: 1.65, color: colors.text }}>
                                                {c.body}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};
